import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { cn } from "../../lib/cn";

/**
 * Promise-based replacements for window.prompt / confirm / alert.
 *
 * The native dialogs were used throughout the catalog tree. Besides looking
 * unstyled, they block the main thread, can't be themed, are suppressed by
 * some browsers, and give no way to validate input before closing. This keeps
 * the same await-a-value call shape so call sites stay readable:
 *
 *   const name = await dialogs.prompt({ title: "New folder", label: "Name" });
 *   if (!name) return;
 */

export type PromptOptions = {
  title: string;
  description?: string;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  /** Return an error string to block submission, or null when valid. */
  validate?: (value: string) => string | null;
};

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
};

export type NotifyOptions = {
  title: string;
  description?: string;
  tone?: "default" | "danger";
};

type DialogsApi = {
  prompt: (options: PromptOptions) => Promise<string | null>;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  notify: (options: NotifyOptions) => Promise<void>;
};

const DialogsContext = createContext<DialogsApi | null>(null);

export function useDialogs(): DialogsApi {
  const ctx = useContext(DialogsContext);
  if (!ctx) {
    throw new Error("useDialogs must be used within a <DialogsProvider>");
  }
  return ctx;
}

type ActiveDialog =
  | { kind: "prompt"; options: PromptOptions }
  | { kind: "confirm"; options: ConfirmOptions }
  | { kind: "notify"; options: NotifyOptions }
  | null;

export function DialogsProvider({ children }: { children: React.ReactNode }) {
  const [active, setActive] = useState<ActiveDialog>(null);
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Holds the resolver for whichever dialog is currently open.
  const resolverRef = useRef<((result: unknown) => void) | null>(null);

  const settle = useCallback((result: unknown) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setActive(null);
    setError(null);
    setValue("");
    resolve?.(result);
  }, []);

  const api = useMemo<DialogsApi>(
    () => ({
      prompt: (options) =>
        new Promise<string | null>((resolve) => {
          resolverRef.current = resolve as (result: unknown) => void;
          setValue(options.initialValue ?? "");
          setError(null);
          setActive({ kind: "prompt", options });
        }),

      confirm: (options) =>
        new Promise<boolean>((resolve) => {
          resolverRef.current = resolve as (result: unknown) => void;
          setActive({ kind: "confirm", options });
        }),

      notify: (options) =>
        new Promise<void>((resolve) => {
          resolverRef.current = resolve as (result: unknown) => void;
          setActive({ kind: "notify", options });
        }),
    }),
    [],
  );

  // Cancelling always yields the "nothing happened" value for that dialog kind.
  const handleClose = useCallback(() => {
    if (!active) return;
    settle(active.kind === "confirm" ? false : active.kind === "prompt" ? null : undefined);
  }, [active, settle]);

  // Accepts an optional event so it can serve both the form's onSubmit and the
  // footer button's onClick.
  const handlePromptSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (active?.kind !== "prompt") return;

      const trimmed = value.trim();
      if (!trimmed) {
        setError("This field is required.");
        return;
      }

      const validationError = active.options.validate?.(trimmed) ?? null;
      if (validationError) {
        setError(validationError);
        return;
      }

      settle(trimmed);
    },
    [active, value, settle],
  );

  return (
    <DialogsContext.Provider value={api}>
      {children}

      {active?.kind === "prompt" && (
        <Modal
          open
          onClose={handleClose}
          title={active.options.title}
          description={active.options.description}
          footer={
            <>
              <Button size="md" variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button size="md" variant="primary" onClick={() => handlePromptSubmit()}>
                {active.options.confirmLabel ?? "Confirm"}
              </Button>
            </>
          }
        >
          <form onSubmit={handlePromptSubmit}>
            {active.options.label && (
              <label
                htmlFor="schemunda-prompt-input"
                className="text-ink-muted mb-1.5 block text-xs font-semibold tracking-wide uppercase"
              >
                {active.options.label}
              </label>
            )}
            <input
              id="schemunda-prompt-input"
              data-autofocus
              type="text"
              value={value}
              placeholder={active.options.placeholder}
              onChange={(event) => {
                setValue(event.target.value);
                if (error) setError(null);
              }}
              className={cn(
                "text-ink placeholder:text-ink-subtle h-10 w-full rounded-lg border px-3 text-sm",
                "transition-colors duration-(--duration-instant) ease-(--ease-standard)",
                "focus:outline-none",
                error
                  ? "border-danger bg-danger-soft/40"
                  : "border-line bg-surface-sunken focus:border-accent focus:bg-surface",
              )}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? "schemunda-prompt-error" : undefined}
            />
            {error && (
              <p
                id="schemunda-prompt-error"
                className="text-danger animate-fade-in mt-1.5 text-xs font-medium"
              >
                {error}
              </p>
            )}
            {/* Enables Enter-to-submit without a visible duplicate button. */}
            <button type="submit" className="hidden" aria-hidden="true" />
          </form>
        </Modal>
      )}

      {active?.kind === "confirm" && (
        <Modal
          open
          onClose={handleClose}
          tone={active.options.tone}
          dismissOnBackdrop={active.options.tone !== "danger"}
          title={active.options.title}
          description={active.options.description}
          footer={
            <>
              <Button size="md" variant="ghost" onClick={handleClose}>
                {active.options.cancelLabel ?? "Cancel"}
              </Button>
              <Button
                size="md"
                variant={active.options.tone === "danger" ? "danger" : "primary"}
                onClick={() => settle(true)}
                data-autofocus
              >
                {active.options.confirmLabel ?? "Confirm"}
              </Button>
            </>
          }
        />
      )}

      {active?.kind === "notify" && (
        <Modal
          open
          onClose={handleClose}
          tone={active.options.tone}
          title={active.options.title}
          description={active.options.description}
          footer={
            <Button size="md" variant="primary" onClick={handleClose} data-autofocus>
              OK
            </Button>
          }
        />
      )}
    </DialogsContext.Provider>
  );
}
